/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Fila Square: nombre · categoría/código · chip · precio.
 * Chip solo estados notables. Con filtro incompletos → meta = motivos.
 */
import React from 'react';
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import type { TicketListRowGroup } from '../../shared/ticket/TicketListRow';
import { matchesSearchText } from '../../shared/format/searchText';
import type { CatalogProductRow } from '../domain/catalogTypes';
import { getCatalogProductStatus, getCatalogProductStockSignal } from '../domain/catalogProductStockSignal';
import { getProductCompletenessIssues } from '../domain/catalogProductCompleteness';

type CatalogProductCardProps = {
  product: CatalogProductRow;
  categoryLabel: string;
  formatCurrency: (value: number) => string;
  onOpenDetail: (product: CatalogProductRow) => void;
  group?: TicketListRowGroup;
  /** Filtro incompletos activo → meta = motivos cortos. */
  showIncompleteMeta?: boolean;
  /** Búsqueda activa → priorizar código/SKU que matchea en la meta. */
  searchQuery?: string;
  /** Label del chip de ítem visible online (copy-pack por rubro). */
  visibleOnlineLabel?: string;
  /** Sin capability inventory: no mostrar chips de stock bajo / sin stock. */
  hideStockSignals?: boolean;
  /** Capability recipes — oculta chip Receta si está apagada. */
  recipesEnabled?: boolean;
};

function resolveListMeta(
  product: CatalogProductRow,
  categoryLabel: string,
  showIncompleteMeta: boolean,
  searchQuery: string,
): string {
  const isService = product.itemType === 'service' || product.isMisc;
  const category = categoryLabel || (isService ? 'Servicio' : null);
  const sku = product.sku != null ? String(product.sku).trim() : '';
  const barcode = product.barcode != null ? String(product.barcode).trim() : '';
  const q = searchQuery.trim();

  let code: string | null = null;
  if (q) {
    if (barcode && matchesSearchText(barcode, q)) code = barcode;
    else if (sku && matchesSearchText(sku, q)) code = sku;
  }
  if (!code) code = sku || barcode || null;

  const parts = [category, code].filter(Boolean) as string[];

  // Filtro «Incompletos»: meta = categoría · código + cada faltante una sola
  // vez (el chip ya no lo repite — ver chipLabel más abajo).
  if (showIncompleteMeta) {
    for (const issue of getProductCompletenessIssues(product)) {
      if (!parts.includes(issue.label)) parts.push(issue.label);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : '—';
}

function CatalogProductCardInner({
  product,
  categoryLabel,
  formatCurrency,
  onOpenDetail,
  group = 'solo',
  showIncompleteMeta = false,
  searchQuery = '',
  visibleOnlineLabel = 'En menú',
  hideStockSignals = false,
  recipesEnabled = true,
}: CatalogProductCardProps) {
  const isService = product.itemType === 'service' || product.isMisc;
  const metaText = resolveListMeta(product, categoryLabel, showIncompleteMeta, searchQuery);
  const hasPrice = product.price > 0;
  const priceText = hasPrice ? formatCurrency(product.price) : '—';
  const status = getCatalogProductStatus(product);
  const stockSignal = getCatalogProductStockSignal(product);

  // Proto: chip solo notables. Prioridad: stock/pausa → sin precio → servicio → en menú.
  // Si la meta ya muestra el mismo motivo (filtro Incompletos), no duplicar en chip.
  const chipAlreadyInMeta = (label: string) => metaText.includes(label);
  let chipLabel: string | null = null;
  let chipTone: 'default' | 'warn' | 'bad' = 'default';
  if (!hideStockSignals && status.tone === 'stock_empty' && !chipAlreadyInMeta(status.label)) {
    chipLabel = status.label;
    chipTone = 'bad';
  } else if (
    !hideStockSignals &&
    status.tone === 'stock_low' &&
    !chipAlreadyInMeta(status.label)
  ) {
    chipLabel = status.label;
    chipTone = 'warn';
  } else if (status.tone === 'paused' && !chipAlreadyInMeta(status.label)) {
    chipLabel = status.label;
    chipTone = 'warn';
  } else if (
    !hideStockSignals &&
    !isService &&
    (stockSignal.tone === 'warning' || stockSignal.tone === 'danger') &&
    !chipAlreadyInMeta(stockSignal.label)
  ) {
    chipLabel = stockSignal.label;
    chipTone = stockSignal.tone === 'danger' ? 'bad' : 'warn';
  } else if (!hasPrice) {
    chipLabel = 'Sin precio';
    chipTone = 'warn';
  } else if (product.isCombo) {
    chipLabel = 'Combo';
  } else if (recipesEnabled && product.hasRecipe) {
    chipLabel = 'Receta';
  } else if (isService || status.tone === 'service') {
    chipLabel = 'Servicio';
  } else if (product.menuAvailable) {
    chipLabel = visibleOnlineLabel;
  }

  return (
    <TicketListRow
      title={product.name}
      meta={metaText}
      leadingImage={product.imageUrl ?? null}
      leadingImageResizeMode="contain"
      value={priceText}
      chipLabel={chipLabel}
      chipTone={chipTone}
      onPress={() => onOpenDetail(product)}
      accessibilityLabel={`${product.name}, ${priceText}. Abrir detalle`}
      testID={`catalog.product.${product.id}`}
      group={group}
    />
  );
}

export const CatalogProductCard = React.memo(CatalogProductCardInner);
